import { request } from '@umijs/max';

export interface HomeSection {
  section_code: string;
  section_name: string;
  section_type: string;
  title?: string;
  subtitle?: string;
  background_media_id?: number | null;
  mobile_background_media_id?: number | null;
  primary_cta_text?: string;
  primary_cta_href?: string;
  secondary_cta_text?: string;
  secondary_cta_href?: string;
  status: number;
  sort: number;
}

export interface PageItem {
  page_code: string;
  title: string;
  subtitle?: string;
  summary?: string;
  content?: string;
  cover_media_id?: number;
  status: number;
  sort: number;
}

export interface ArticleItem {
  id: number;
  article_type: string;
  category_id?: number;
  title: string;
  summary?: string;
  content?: string;
  cover_media_id?: number;
  status: number;
  published_at?: string;
  sort: number;
}

export interface ContactSubmissionItem {
  id: number;
  name: string;
  phone?: string;
  email?: string;
  company?: string;
  message?: string;
  source?: string;
  status: number;
  submitted_at: string;
}

export interface StoreItem {
  id: number;
  city_name: string;
  store_name: string;
  cover_media_id?: number;
  address?: string;
  phone?: string;
  business_hours?: string;
  status: number;
  sort: number;
}

export interface ProductCategoryItem {
  id: number;
  category_code: string;
  category_name: string;
  summary?: string;
  cover_media_id?: number;
  status: number;
  sort: number;
}

export interface ProductItem {
  id: number;
  category_id?: number | null;
  category_code?: string;
  category_name?: string;
  product_code: string;
  name: string;
  subtitle?: string;
  summary?: string;
  description?: string;
  cover_media_id?: number | null;
  gallery_media_ids?: number[];
  tags?: string[];
  material?: string;
  weight?: string;
  craft?: string;
  size?: string;
  scene?: string;
  price_note?: string;
  design_meaning?: string;
  craft_note?: string;
  quality_note?: string;
  status: number;
  is_featured: boolean;
  is_hot: boolean;
  sort: number;
}

export interface PageResult<T> {
  items: T[];
  page: number;
  size: number;
  total: number;
}

export async function getHomeSections() {
  return request<{ code: number; data: HomeSection[] }>('/api/v1/content/home/sections', {
    method: 'GET',
  });
}

export async function replaceHomeSections(items: HomeSection[]) {
  return request<{ code: number; data: HomeSection[] }>('/api/v1/content/home/sections', {
    method: 'PUT',
    data: { items },
  });
}

export async function getPageList(params: {
  current?: number;
  pageSize?: number;
  keyword?: string;
  status?: number;
}) {
  return request<{ code: number; data: PageResult<PageItem> }>('/api/v1/content/pages', {
    method: 'GET',
    params,
  });
}

export async function getPageByCode(pageCode: string) {
  return request<{ code: number; data: PageItem }>(`/api/v1/content/pages/${pageCode}`, {
    method: 'GET',
  });
}

export async function getPublicPageByCode(pageCode: string) {
  return request<{ code: number; data: PageItem }>(`/api/v1/public/site/pages/${pageCode}`, {
    method: 'GET',
  });
}

export async function createPage(data: PageItem) {
  return request<{ code: number; data: PageItem }>('/api/v1/content/pages', {
    method: 'POST',
    data,
  });
}

export async function updatePage(pageCode: string, data: Partial<PageItem>) {
  return request<{ code: number; data: PageItem }>(`/api/v1/content/pages/${pageCode}`, {
    method: 'PUT',
    data,
  });
}

export async function deletePage(pageCode: string) {
  return request<{ code: number; data: { deleted: boolean } }>(`/api/v1/content/pages/${pageCode}`, {
    method: 'DELETE',
  });
}

export async function getArticleList(params: {
  current?: number;
  pageSize?: number;
  keyword?: string;
  article_type?: string;
  status?: number;
}) {
  return request<{ code: number; data: PageResult<ArticleItem> }>('/api/v1/content/articles', {
    method: 'GET',
    params,
  });
}

export async function createArticle(data: Partial<ArticleItem> & { article_type: string; title: string }) {
  return request<{ code: number; data: ArticleItem }>('/api/v1/content/articles', {
    method: 'POST',
    data,
  });
}

export async function updateArticle(id: number, data: Partial<ArticleItem>) {
  return request<{ code: number; data: ArticleItem }>(`/api/v1/content/articles/${id}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteArticle(id: number) {
  return request<{ code: number; data: { deleted: boolean } }>(`/api/v1/content/articles/${id}`, {
    method: 'DELETE',
  });
}

export async function getContactSubmissionList(params: {
  current?: number;
  pageSize?: number;
  status?: number;
}) {
  return request<{ code: number; data: PageResult<ContactSubmissionItem> }>('/api/v1/content/contact-submissions', {
    method: 'GET',
    params,
  });
}

export async function updateContactSubmissionStatus(id: number, status: number) {
  return request<{ code: number; data: ContactSubmissionItem }>(`/api/v1/content/contact-submissions/${id}/status`, {
    method: 'PUT',
    data: { status },
  });
}

export async function getStoreList(params: {
  current?: number;
  pageSize?: number;
  keyword?: string;
  city_name?: string;
  status?: number;
}) {
  return request<{ code: number; data: PageResult<StoreItem> }>('/api/v1/content/stores', {
    method: 'GET',
    params,
  });
}

export async function createStore(data: Omit<StoreItem, 'id'>) {
  return request<{ code: number; data: StoreItem }>('/api/v1/content/stores', {
    method: 'POST',
    data,
  });
}

export async function updateStore(id: number, data: Partial<StoreItem>) {
  return request<{ code: number; data: StoreItem }>(`/api/v1/content/stores/${id}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteStore(id: number) {
  return request<{ code: number; data: { deleted: boolean } }>(`/api/v1/content/stores/${id}`, {
    method: 'DELETE',
  });
}

export async function getProductCategoryList(params: {
  current?: number;
  pageSize?: number;
  keyword?: string;
  status?: number;
}) {
  return request<{ code: number; data: PageResult<ProductCategoryItem> }>('/api/v1/content/product-categories', {
    method: 'GET',
    params,
  });
}

export async function createProductCategory(data: Omit<ProductCategoryItem, 'id'>) {
  return request<{ code: number; data: ProductCategoryItem }>('/api/v1/content/product-categories', {
    method: 'POST',
    data,
  });
}

export async function updateProductCategory(id: number, data: Partial<ProductCategoryItem>) {
  return request<{ code: number; data: ProductCategoryItem }>(`/api/v1/content/product-categories/${id}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteProductCategory(id: number) {
  return request<{ code: number; data: { deleted: boolean } }>(`/api/v1/content/product-categories/${id}`, {
    method: 'DELETE',
  });
}

export async function getProductList(params: {
  current?: number;
  pageSize?: number;
  keyword?: string;
  category_id?: number;
  status?: number;
  is_featured?: boolean;
  is_hot?: boolean;
}) {
  return request<{ code: number; data: PageResult<ProductItem> }>('/api/v1/content/products', {
    method: 'GET',
    params,
  });
}

export async function createProduct(data: Omit<ProductItem, 'id' | 'category_code' | 'category_name'>) {
  return request<{ code: number; data: ProductItem }>('/api/v1/content/products', {
    method: 'POST',
    data,
  });
}

export async function updateProduct(id: number, data: Partial<ProductItem>) {
  return request<{ code: number; data: ProductItem }>(`/api/v1/content/products/${id}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteProduct(id: number) {
  return request<{ code: number; data: { deleted: boolean } }>(`/api/v1/content/products/${id}`, {
    method: 'DELETE',
  });
}
